import { Alert, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MonitoringMap from "../components/monitoringSection/monitoringMap.component";
import LogoContainer from "../components/monitoringSection/logoContainer.component";
import dayjs from "dayjs";
import { fetchSensorAverageDataStart } from "../store/features/customer/sensor/sensorAverageDataSlice";
import MonitoringAverageChart from "../components/monitoringSection/monitoringAverageChart.component";
import MonitoringSensorChart from "../components/monitoringSection/monitoringSensorChart.component";
import { fetchSensorDataStart } from "../store/features/customer/sensor/sensorDataSlice";
import { sensorIsHoverStart } from "../store/features/customer/sensor/sensorSlice";
import DashboardDetails from "../components/monitoringSection/dashboardDetailsContainer.component";
import { fetchFieldsStart } from "../store/features/customer/fields/fieldsSlice";
import { fetchSensorsStart } from "../store/features/customer/instruments/sensorsSlice";
import useMonitoringAuth from "../hook/useMonitoringAuth";
import { formatToISO } from "../components/chart/utils";
import { isWeatherStation } from "../config/sensors.config";

const ROTATION_INTERVAL_MS = 30000;
const EMPTY_LIST = [];
const MEASURE_MAP = {
  soil_electrical_conductivity: "soil_ec",
};
const SUFFIX_TO_EXTRACT = ["temperature", "moisture"];

const normalizeMeasure = (measure) => {
  if (!measure) return measure;
  if (MEASURE_MAP[measure]) return MEASURE_MAP[measure];

  const suffix = SUFFIX_TO_EXTRACT.find((s) => measure.endsWith(s));
  return suffix || measure;
};

const MonitoringSection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { accessToken, error: authError } = useMonitoringAuth();
  const sensorsData =
    useSelector((state) => state.sensors?.data?.data) || EMPTY_LIST;
  const fieldsData =
    useSelector((state) => state.fields?.data?.data) || EMPTY_LIST;
  const fieldsLoading = useSelector((state) => state.fields?.isLoading);
  const fieldsError = useSelector((state) => state.fields?.error);

  const [sensorIndex, setSensorIndex] = useState(0);

  const field = accessToken
    ? fieldsData.find(
        (candidate) =>
          candidate.user?.company_name?.trim().toLowerCase() === "uniss",
      ) || null
    : null;
  const sensors = useMemo(() => {
    const sensorIds = new Set((field?.sensors || []).map((sensor) => sensor.id));
    return sensorsData.filter((sensor) => sensorIds.has(sensor.id));
  }, [field, sensorsData]);

  const currentSensor = sensors[sensorIndex % sensors.length];
  const currentSensorId = currentSensor?.id || "";
  const source = isWeatherStation(currentSensor) ? "weather" : "sensors";
  const averageScope = currentSensorId
    ? `${field.id}:monitoring:${source}:${currentSensorId}:average`
    : undefined;
  const measures =
    currentSensor?.sensor_type?.measured_parameters || EMPTY_LIST;

  const { from, to } = useMemo(() => {
    const now = dayjs().utc();
    return {
      from: formatToISO(now.subtract(1, "day")),
      to: formatToISO(now),
    };
  }, []);

  useEffect(() => {
    if (!accessToken) return;

    dispatch(fetchFieldsStart({ accessToken }));
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (!accessToken || !field?.id) return;
    dispatch(fetchSensorsStart({ accessToken, field: field.id }));
    setSensorIndex(0);
  }, [accessToken, dispatch, field?.id]);

  useEffect(() => {
    if (sensors.length === 0) return;

    const interval = setInterval(() => {
      setSensorIndex((prev) => prev + 1);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [sensors.length]);

  const fetchSensorData = useCallback(() => {
    if (!accessToken || !field?.id || measures?.length === 0) return;

    measures?.forEach((measure) => {
      dispatch(
        fetchSensorAverageDataStart({
          accessToken,
          fieldId: field.id,
          from,
          to,
          measure,
          source,
          scope: averageScope,
        })
      );
      dispatch(
        fetchSensorDataStart({
          accessToken,
          id: currentSensorId,
          from,
          to,
          measure: normalizeMeasure(measure),
          filtered: false,
        })
      );
    });
  }, [accessToken, dispatch, field?.id, from, to, currentSensorId, measures, source, averageScope]);

  useEffect(() => {
    fetchSensorData();
  }, [fetchSensorData]);

  useEffect(() => {
    if (currentSensorId) {
      dispatch(sensorIsHoverStart(currentSensorId));
    }
  }, [currentSensorId, dispatch]);

  if (!accessToken || !field) {
    const error = authError || fieldsError;
    return (
      <Box p={3}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Typography role="status">
            {!accessToken || fieldsLoading
              ? "Caricamento monitoraggio Uniss..."
              : "Nessun terreno Uniss disponibile per il monitoraggio."}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        backgroundColor: theme.palette.custom.backgroundColor,
        padding: 2,
        boxSizing: "border-box",
      }}
    >
      <Box
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            height: "500px",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <DashboardDetails />
          <MonitoringMap sensors={sensors} field={field} />
        </Paper>
        <Stack spacing={2} direction="row">
          <MonitoringAverageChart measures={measures} sensors={sensors} scope={averageScope} />
          <MonitoringSensorChart
            measures={measures}
            sensors={sensors}
            currentSensorId={currentSensorId}
          />
        </Stack>
        <Paper elevation={0} sx={{ width: "100%", height: "130px" }}>
          <LogoContainer />
        </Paper>
      </Box>
    </Box>
  );
};

export default MonitoringSection;
